import { BankAccount, IbanFormProps } from '../utils/types';
import React, { useEffect, useState } from 'react';
import { IBANInput } from './IbanInput';
import i18n from '../i18n';

export const IbanForm: React.FC<IbanFormProps> = ({
  onSubmit,
  details,
  askBankName = true,
  title = i18n.t('ibanForm.title'),
  subtitle = i18n.t('ibanForm.subtitle'),
  submitButtonText = i18n.t('ibanForm.submit'),
  className = '',
  showTitle = true,
  onCancel,
  showButtons = true,
}) => {
  const [formData, setFormData] = useState<BankAccount>({
    bank: details?.bank || '',
    customer: details?.customer || '',
    iban: details?.iban || '',
  });
  const [iban, setIban] = useState(details?.iban || '');
  const [isIbanValid, setIsIbanValid] = useState<boolean | null>(null);
  const canSubmit = isIbanValid === true && iban.trim() !== '';

  // Reset form when details change
  useEffect(() => {
    setFormData({
      bank: details?.bank || '',
      customer: details?.customer || '',
      iban: details?.iban || '',
    });
    setIban(details?.iban || '');
  }, [details]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      bank: askBankName === false ? '' : formData.bank,
      iban: iban,
    };
    onSubmit?.(submitData);
  };

  // #108 (S3): the form on the design system (xc-form / xc-field / xc-input);
  // the validation logic and the canSubmit gate are unchanged
  return (
    <form
      className={className ? `xc-form ${className}` : 'xc-form'}
      onSubmit={handleSubmit}
    >
      {showTitle && (
        <div>
          <h3 className="xc-card__title">{title}</h3>
          <p className="xc-field__help">{subtitle}</p>
        </div>
      )}

      {/* Bank Name */}
      {askBankName !== false && (
        <div className="xc-field">
          <label htmlFor="bank-input" className="xc-field__label">
            {i18n.t('ibanForm.bankName')}
          </label>
          <input
            id="bank-input"
            type="text"
            className="xc-input"
            value={formData.bank}
            name="bank"
            onChange={handleInputChange}
            placeholder={
              i18n.t('ibanForm.enterBankName') ?? 'Enter your bank name'
            }
          />
        </div>
      )}

      {/* Account Holder Name */}
      <div className="xc-field">
        <label htmlFor="customer-input" className="xc-field__label">
          {i18n.t('ibanForm.accountName')}
        </label>
        <input
          id="customer-input"
          type="text"
          className="xc-input"
          value={formData.customer}
          name="customer"
          onChange={handleInputChange}
          placeholder={
            i18n.t('ibanForm.customerName') ?? 'Enter your account name'
          }
        />
      </div>

      {/* IBAN */}
      <div className="xc-field">
        <label htmlFor="iban-input" className="xc-field__label">
          {i18n.t('ibanForm.iban')} <span className="xc-field__req">*</span>
        </label>
        <IBANInput
          id="iban-input"
          value={iban}
          onChange={setIban}
          onValidationChange={setIsIbanValid}
          placeholder={
            i18n.t('ibanForm.ibanExample') ?? 'RO49 AAAA 1B31 0075 9384 0000'
          }
          showCountryCode={true}
          showDetailedErrors={true}
          required
        />
      </div>

      {/* Action Buttons */}
      {showButtons && (
        <div className="xc-form__actions">
          {onCancel && (
            <button type="button" className="xc-btn" onClick={onCancel}>
              {i18n.t('buttons.cancel')}
            </button>
          )}
          <button
            type="submit"
            className="xc-btn xc-btn--primary"
            disabled={!canSubmit}
          >
            {submitButtonText}
          </button>
        </div>
      )}
    </form>
  );
};
